import * as React from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { Droppable, DragDropContext } from 'react-beautiful-dnd';
import { Row } from 'react-bootstrap';
import type { WrappedFieldArrayProps } from 'redux-form';

import type { EmployeeOptionVM } from 'ab-viewModels/employee/extendedOption.viewModel';
import type EquipmentViewModel from 'ab-viewModels/equipment.viewModel';
import type TemporaryEmployeeOptionVM from 'ab-viewModels/temporaryEmployee/temporaryEmployeeOption.viewModel';

import type { AssignableResourceType } from '@acceligentllc/shared/enums/assignableResourceType';

import AddResourceLookupButton from './AddResourceLookupButton';
import DraggableResourceLookup from './DraggableResourceLookup';
import type WorkOrderFM from '../../formModel';

export interface OwnProps {
	change: (fieldName: string, resources: WorkOrderFM['workOrderResourceLookups']) => void;
	disabled?: boolean;
	dueDate: string;
	employees: EmployeeOptionVM[];
	equipment: EquipmentViewModel[];
	temporaryEmployees: TemporaryEmployeeOptionVM[];
	isAllowedToManagePerDiems: boolean;
	isCancelled: boolean;
	loadEmployees: (isLazyLoaded: boolean) => void;
	loadEquipment: (isLazyLoaded: boolean) => void;
	loadTemporaryEmployees: (isLazyLoaded: boolean) => void;
	resetTimer: () => void;
	selector: (fieldName: string) => WorkOrderFM['workOrderResourceLookups'];
	showResourceAssignConfirmationModal: (resourceType: AssignableResourceType) => void;
	workOrderId: number;
	foremanEnabled: boolean;
}

type Props = OwnProps & WrappedFieldArrayProps<WorkOrderFM['workOrderResourceLookups'][0]>;

export default class ResourceLookups extends React.PureComponent<Props> {

	static mapUpdatedResourceLookups = (worl: WorkOrderFM['workOrderResourceLookups'][0], index: number) => {
		return {
			...worl,
			index,
		};
	};

	componentDidUpdate(prevProps: Props) {
		if (prevProps.foremanEnabled && !this.props.foremanEnabled) {
			this.clearForeman();
		}
	}

	clearForeman = () => {
		const { selector, change } = this.props;

		const resourceLookups = selector('workOrderResourceLookups') as WorkOrderFM['workOrderResourceLookups'];

		if (!resourceLookups.some((_lookup) => _lookup.isCrewLead)) {
			return;
		}

		const updatedResourceLookups = resourceLookups.map((_lookup) => ({
			..._lookup,
			isCrewLead: false,
		}));

		change('workOrderResourceLookups', updatedResourceLookups);
	};

	updatePerDiemAmount = (propName: string, index: number, perDiemCheck: boolean) => {
		const { selector, change } = this.props;

		const resourceLookups = selector('workOrderResourceLookups') as WorkOrderFM['workOrderResourceLookups'];
		const updatedLookups = resourceLookups;
		// In case perDiem is unselected, we want to set amount to null
		if (!perDiemCheck) {
			updatedLookups[index].perDiem = false;
			updatedLookups[index].perDiemAmount = null;
			change('workOrderResourceLookups', updatedLookups);
		} else {
			// In case perDiem is selected, we want to set amount to 0, unless it already has some value
			if (!updatedLookups[index].perDiemAmount) {
				updatedLookups[index].perDiem = true;
				updatedLookups[index].perDiemAmount = '0';
				change('workOrderResourceLookups', updatedLookups);
			}
		}
	};

	addWorkOrderResourceLookup = (propName: string) => {
		const { fields } = this.props;
		const field = {
			[propName]: null,
			index: fields.length,
			isCrewLead: false,
		} as Partial<WorkOrderFM['workOrderResourceLookups'][0]>;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		fields.push(field as any);
	};

	updateForeman = (index: number, isCrewLead: boolean) => {
		const { selector, change } = this.props;

		const resourceLookups = selector('workOrderResourceLookups') as WorkOrderFM['workOrderResourceLookups'];

		const updatedResourceLookups = resourceLookups.map((_resourceLookup, _index) => ({
			..._resourceLookup,
			isCrewLead: !!_resourceLookup.employeeId && _index === index && isCrewLead,
		}));

		change('workOrderResourceLookups', updatedResourceLookups);
	};

	onRemove = (index: number) => {
		const { selector, change } = this.props;

		const resourceLookups = selector('workOrderResourceLookups') as WorkOrderFM['workOrderResourceLookups'];

		resourceLookups.splice(index, 1);

		const updatedResourceLookups = resourceLookups.map(ResourceLookups.mapUpdatedResourceLookups);

		change('workOrderResourceLookups', updatedResourceLookups);
	};

	onDragEnd = ({ source, destination }: DropResult) => {
		const { selector, change } = this.props;

		if (!destination) {
			return;
		}

		const resourceLookups = selector('workOrderResourceLookups') as WorkOrderFM['workOrderResourceLookups'];

		if (source.index === destination.index) {
			return;
		}

		const [movedItem] = resourceLookups.splice(source.index, 1);

		resourceLookups.splice(destination.index, 0, movedItem);

		const updatedResourceLookups = resourceLookups.map(ResourceLookups.mapUpdatedResourceLookups);

		change('workOrderResourceLookups', updatedResourceLookups);
	};

	render() {
		const {
			disabled,
			dueDate,
			employees,
			equipment,
			temporaryEmployees,
			fields,
			isAllowedToManagePerDiems,
			isCancelled,
			loadEmployees,
			loadEquipment,
			loadTemporaryEmployees,
			resetTimer,
			workOrderId,
			showResourceAssignConfirmationModal,
			foremanEnabled,
		} = this.props;

		return (
			<DragDropContext onDragEnd={this.onDragEnd}>
				<div className="resource-lookup">
					<Droppable
						droppableId="RESOURCE_LOOKUPS"
						type="RESOURCE_LOOKUPS"
					>
						{(droppableProvided) => (
							<div className="resource-lookup__list" ref={droppableProvided.innerRef}>
								{fields.map((_resourceLookupPropName, _index) => {
									return (
										<DraggableResourceLookup
											disabled={disabled}
											draggableId={`workOrderResourceLookup#${_index}`}
											dueDate={dueDate}
											employees={employees}
											equipment={equipment}
											foremanEnabled={foremanEnabled}
											index={_index}
											isAllowedToManagePerDiems={isAllowedToManagePerDiems}
											isCancelled={isCancelled}
											key={`workOrderResourceLookup#${_index}`}
											loadEmployees={loadEmployees}
											loadEquipment={loadEquipment}
											loadTemporaryEmployees={loadTemporaryEmployees}
											removeWorkOrderResourceLookup={this.onRemove}
											resetTimer={resetTimer}
											resourceLookup={fields.get(_index) as WorkOrderFM['workOrderResourceLookups'][0]}
											resourceLookupPropName={_resourceLookupPropName}
											showResourceAssignConfirmationModal={showResourceAssignConfirmationModal}
											temporaryEmployees={temporaryEmployees}
											updateForeman={this.updateForeman}
											updatePerDiemAmount={this.updatePerDiemAmount}
											workOrderId={workOrderId}
										/>
									);
								})}
							</div>
						)}
					</Droppable>
					<Row className="row--non-padded row--flex">
						<AddResourceLookupButton
							addWorkOrderResourceLookup={this.addWorkOrderResourceLookup}
							disabled={!!disabled}
							label="Add Labor"
							propName="employeeId"
						/>
						<AddResourceLookupButton
							addWorkOrderResourceLookup={this.addWorkOrderResourceLookup}
							disabled={!!disabled}
							label="Add Equipment"
							propName="equipmentId"
						/>
						<AddResourceLookupButton
							addWorkOrderResourceLookup={this.addWorkOrderResourceLookup}
							disabled={!!disabled}
							label="Add Temporary Labor"
							propName="temporaryEmployeeId"
						/>
						<AddResourceLookupButton
							addWorkOrderResourceLookup={this.addWorkOrderResourceLookup}
							disabled={!!disabled}
							label="Add Labor Placeholder"
							propName="wageRateId"
						/>
						<AddResourceLookupButton
							addWorkOrderResourceLookup={this.addWorkOrderResourceLookup}
							disabled={!!disabled}
							label="Add Equipment Placeholder"
							propName="equipmentCostId"
						/>
					</Row>
				</div>
			</DragDropContext>
		);
	}
}
